import React, { useState, useEffect, useMemo } from 'react';
import { CheckCircle, FileText, Wallet, Split, AlertCircle, Building2 } from 'lucide-react';
import { useApp } from '../../../context/AppContext';
import apiService from '../../../services/apiService';
import Modal from '../../../components/ui/Modal';
import { formatMoney, formatDate } from '../../../utils/formatters';

const LIMITE_EFECTIVO = 2000; // Límite para facturación en efectivo

const NuevaFacturaModal = ({
  cliente,
  datosFiscalesLista = [],
  datosFiscalesSeleccionado,
  onCambiarDatoFiscal,
  ventasIds,
  ventas,
  tipoVenta = 'venta', // 'venta' o 'mostrador'
  pago,
  catalogos,
  emisores = [],
  onClose,
  onSuccess,
  tipoPago = false
}) => {
  // Alias para compatibilidad
  const datosFiscales = datosFiscalesSeleccionado;
  const { showToast, setLoading, loading } = useApp();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    uso_cfdi: datosFiscalesSeleccionado?.uso_cfdi || '',
    forma_pago: '',
    metodo_pago: 'PUE',
    emisor_id: emisores.length === 1 ? String(emisores[0].id) : ''
  });
  const [numFacturas, setNumFacturas] = useState(1);
  const [errors, setErrors] = useState({});
  const [facturasCreadas, setFacturasCreadas] = useState([]);

  // Calcular totales segun el tipo
  const totales = tipoPago && pago
    ? {
        subtotal: parseFloat(pago.monto_aplicado) / 1.16,
        impuestos: parseFloat(pago.monto_aplicado) - (parseFloat(pago.monto_aplicado) / 1.16),
        total: parseFloat(pago.monto_aplicado)
      }
    : {
        subtotal: ventas?.reduce((sum, v) => sum + parseFloat(v.subtotal || 0), 0) || 0,
        impuestos: ventas?.reduce((sum, v) => sum + parseFloat(v.impuesto || 0), 0) || 0,
        total: ventas?.reduce((sum, v) => sum + parseFloat(v.total || 0), 0) || 0
      };

  // Calcular mínimo de facturas necesarias para efectivo
  const minFacturasEfectivo = useMemo(() => {
    return Math.ceil(totales.total / LIMITE_EFECTIVO);
  }, [totales.total]);

  // Calcular distribución de montos por factura
  const distribucionFacturas = useMemo(() => {
    const montoBase = Math.floor((totales.total / numFacturas) * 100) / 100;
    const residuo = Math.round((totales.total - (montoBase * numFacturas)) * 100) / 100;

    return Array.from({ length: numFacturas }, (_, i) => {
      const monto = i === 0 ? montoBase + residuo : montoBase;
      return {
        numero: i + 1,
        total: monto,
        subtotal: monto / 1.16,
        iva: monto - (monto / 1.16)
      };
    });
  }, [numFacturas, totales.total]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Auto-seleccionar emisor si solo hay uno y no se ha seleccionado
  useEffect(() => {
    if (emisores.length === 1 && !formData.emisor_id) {
      setFormData(prev => ({ ...prev, emisor_id: String(emisores[0].id) }));
    }
  }, [emisores]);

  const emisorSeleccionado = emisores.find(e => String(e.id) === String(formData.emisor_id)) || null;

  // Validar paso 3 (configuración de pago)
  const validateStep3 = () => {
    const newErrors = {};

    if (emisores.length > 0 && !formData.emisor_id) {
      newErrors.emisor_id = 'Seleccione un emisor';
    }

    if (!formData.uso_cfdi) {
      newErrors.uso_cfdi = 'Uso de CFDI es requerido';
    }

    if (!formData.forma_pago) {
      newErrors.forma_pago = 'Forma de pago es requerida';
    }

    if (!formData.metodo_pago) {
      newErrors.metodo_pago = 'Metodo de pago es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validar paso 2 (división de factura)
  const validateStep2 = () => {
    // Si es efectivo y el monto supera el límite, verificar división adecuada
    if (formData.forma_pago === '01' && totales.total > LIMITE_EFECTIVO) {
      const maxPorFactura = Math.max(...distribucionFacturas.map(f => f.total));
      if (maxPorFactura > LIMITE_EFECTIVO) {
        showToast(`Para efectivo, cada factura debe ser máximo ${formatMoney(LIMITE_EFECTIVO)}`, 'error');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (step === 2 && !validateStep2()) {
      return;
    }
    if (step === 3 && !validateStep3()) {
      return;
    }
    setStep(prev => prev + 1);
  };

  const handleBack = () => {
    setStep(prev => prev - 1);
  };

  const handleGenerarFacturas = async () => {
    try {
      setLoading(true);

      const currentUser = JSON.parse(localStorage.getItem('current_user') || '{}');
      const facturasGeneradas = [];

      // Crear cada factura
      for (let i = 0; i < numFacturas; i++) {
        const factura = distribucionFacturas[i];

        const payload = {
          cliente_id: cliente.id,
          dato_fiscal_id: datosFiscales?.id || null, // ID del dato fiscal seleccionado
          rfc: datosFiscales?.rfc,
          razon_social: datosFiscales?.razon_social,
          regimen_fiscal: datosFiscales?.regimen_fiscal,
          codigo_postal_fiscal: datosFiscales?.codigo_postal_fiscal,
          uso_cfdi: formData.uso_cfdi,
          forma_pago: formData.forma_pago,
          metodo_pago: formData.metodo_pago,
          usuario_id: currentUser.id || currentUser.usuario_id || 1,
          monto_parcial: factura.total,
          numero_parcialidad: i + 1,
          total_parcialidades: numFacturas,
          emisor_id: emisorSeleccionado?.id || null,
          emisor_llave: emisorSeleccionado?.llave || null
        };

        if (tipoPago && pago) {
          payload.pago_cliente_id = pago.id;
          payload.tipo = 'pago';
        } else {
          payload.venta_ids = ventasIds;
          payload.tipo = tipoVenta === 'mostrador' ? 'mostrador' : 'venta';
        }

        const result = await apiService.crearFactura(payload);

        if (result.success) {
          facturasGeneradas.push(result.data);
        } else {
          showToast(result.message || `Error al crear factura ${i + 1}`, 'error');
          // Si falla una, detenemos el proceso
          if (facturasGeneradas.length > 0) {
            setFacturasCreadas(facturasGeneradas);
            setStep(5);
          }
          return;
        }
      }

      setFacturasCreadas(facturasGeneradas);
      setStep(5);

      const timbradas = facturasGeneradas.filter(f => f.estado === 'timbrada').length;
      const pendientes = facturasGeneradas.length - timbradas;
      if (pendientes > 0) {
        showToast(`${facturasGeneradas.length} factura(s) creada(s). ${timbradas} timbrada(s), ${pendientes} pendiente(s) de timbrado`, 'warning');
      } else {
        showToast(`${facturasGeneradas.length} factura(s) timbrada(s) exitosamente`, 'success');
      }
    } catch (error) {
      console.error('Error creando facturas:', error);
      showToast('Error al crear facturas', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={tipoPago ? "Facturar Pago/Abono" : "Generar Factura CFDI"}
      onClose={onClose}
      size="large"
      className="modal-facturacion-container"
    >
      <div className="modal-facturacion">
        {/* Step 1: Datos Fiscales */}
        {step === 1 && (
          <div className="wizard-content">
            <h3>Datos Fiscales del Cliente</h3>

            {/* Selector de Razón Social cuando hay múltiples */}
            {datosFiscalesLista.length > 1 && (
              <div className="razon-social-selector" style={{
                marginBottom: '1.5rem',
                padding: '1rem',
                background: '#f0f9ff',
                borderRadius: '0.5rem',
                border: '1px solid #bae6fd'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: '600',
                  color: '#0369a1',
                  marginBottom: '0.75rem'
                }}>
                  <Building2 size={18} />
                  Seleccionar Razón Social para Facturar
                </label>
                <select
                  value={datosFiscales?.id || ''}
                  onChange={(e) => onCambiarDatoFiscal(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '0.95rem',
                    borderRadius: '0.375rem',
                    border: '1px solid #7dd3fc',
                    background: 'white',
                    cursor: 'pointer'
                  }}
                >
                  {datosFiscalesLista.map((df) => (
                    <option key={df.id} value={df.id}>
                      {df.rfc} - {df.razon_social} {df.es_principal ? '(Principal)' : ''}
                    </option>
                  ))}
                </select>
                <p style={{ fontSize: '0.8rem', color: '#0284c7', marginTop: '0.5rem', marginBottom: 0 }}>
                  El cliente tiene {datosFiscalesLista.length} razones sociales registradas
                </p>
              </div>
            )}

            <div className="factura-detail-grid">
              <div className="detail-section">
                <h4>Receptor</h4>
                <div className="detail-row">
                  <label>RFC:</label>
                  <span>{datosFiscales?.rfc}</span>
                </div>
                <div className="detail-row">
                  <label>Razon Social:</label>
                  <span>{datosFiscales?.razon_social}</span>
                </div>
                <div className="detail-row">
                  <label>Regimen Fiscal:</label>
                  <span>
                    {catalogos.regimenesFiscales.find(r => r.clave === datosFiscales?.regimen_fiscal)?.descripcion || datosFiscales?.regimen_fiscal}
                  </span>
                </div>
                <div className="detail-row">
                  <label>C.P. Fiscal:</label>
                  <span>{datosFiscales?.codigo_postal_fiscal}</span>
                </div>
              </div>

              <div className="detail-section">
                <h4>{tipoPago ? 'Pago a Facturar' : 'Ventas a Facturar'}</h4>
                {tipoPago && pago ? (
                  <>
                    <div className="detail-row">
                      <label>Folio Pago:</label>
                      <span style={{ fontWeight: 600, color: '#667eea' }}>{pago.folio}</span>
                    </div>
                    <div className="detail-row">
                      <label>Fecha:</label>
                      <span>{formatDate(pago.fecha_pago)}</span>
                    </div>
                    <div className="detail-row">
                      <label>Metodo:</label>
                      <span style={{ textTransform: 'capitalize' }}>{pago.metodo_pago}</span>
                    </div>
                    <div className="detail-row">
                      <label>Cuentas afectadas:</label>
                      <span>{pago.cuentas_afectadas}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="detail-row">
                      <label>Cantidad:</label>
                      <span>{ventas?.length || 0} venta(s)</span>
                    </div>
                    <div className="detail-row">
                      <label>Subtotal:</label>
                      <span>{formatMoney(totales.subtotal)}</span>
                    </div>
                    <div className="detail-row">
                      <label>IVA:</label>
                      <span>{formatMoney(totales.impuestos)}</span>
                    </div>
                  </>
                )}
                <div className="detail-row" style={{ fontWeight: 'bold' }}>
                  <label>Total:</label>
                  <span style={{ color: '#059669' }}>{formatMoney(totales.total)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: División de Factura */}
        {step === 2 && (
          <div className="wizard-content">
            <h3>División de Factura</h3>

            <div className="division-info-box" style={{
              padding: '1rem',
              background: '#fef3c7',
              borderRadius: '0.5rem',
              marginBottom: '1rem',
              border: '1px solid #fcd34d'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <AlertCircle size={20} color="#92400e" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <strong style={{ color: '#92400e' }}>Límite para pagos en efectivo</strong>
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: '#78350f' }}>
                    Para facturas pagadas en efectivo, el monto máximo por factura es de {formatMoney(LIMITE_EFECTIVO)}.
                    {totales.total > LIMITE_EFECTIVO && (
                      <span> Se requieren mínimo <strong>{minFacturasEfectivo} facturas</strong> para este monto.</span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>¿En cuántas facturas desea dividir el monto?</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setNumFacturas(Math.max(1, numFacturas - 1))}
                  style={{
                    width: '40px',
                    height: '40px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    background: 'white',
                    fontSize: '1.25rem',
                    cursor: 'pointer'
                  }}
                >
                  -
                </button>
                <span style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  minWidth: '60px',
                  textAlign: 'center',
                  color: '#667eea'
                }}>
                  {numFacturas}
                </span>
                <button
                  type="button"
                  onClick={() => setNumFacturas(numFacturas + 1)}
                  style={{
                    width: '40px',
                    height: '40px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    background: 'white',
                    fontSize: '1.25rem',
                    cursor: 'pointer'
                  }}
                >
                  +
                </button>
                {totales.total > LIMITE_EFECTIVO && (
                  <button
                    type="button"
                    onClick={() => setNumFacturas(minFacturasEfectivo)}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '1px solid #667eea',
                      borderRadius: '0.375rem',
                      background: '#eef2ff',
                      color: '#667eea',
                      fontSize: '0.875rem',
                      cursor: 'pointer'
                    }}
                  >
                    Usar mínimo ({minFacturasEfectivo})
                  </button>
                )}
              </div>
            </div>

            {/* Preview de distribución */}
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', color: '#374151' }}>
                <Split size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Distribución de Facturas
              </h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: numFacturas <= 3 ? `repeat(${numFacturas}, 1fr)` : 'repeat(3, 1fr)',
                gap: '0.75rem'
              }}>
                {distribucionFacturas.map((factura) => (
                  <div
                    key={factura.numero}
                    style={{
                      padding: '0.75rem',
                      background: '#f9fafb',
                      borderRadius: '0.375rem',
                      border: '1px solid #e5e7eb'
                    }}
                  >
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Factura {factura.numero}
                    </div>
                    <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#059669' }}>
                      {formatMoney(factura.total)}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                      Subtotal: {formatMoney(factura.subtotal)} + IVA: {formatMoney(factura.iva)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Forma de Pago */}
        {step === 3 && (
          <div className="wizard-content">
            <h3>Configuracion de Pago</h3>

            {/* Selector de Emisor */}
            {emisores.length > 0 && (
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label>
                  Emisor <span className="required">*</span>
                </label>
                <select
                  value={formData.emisor_id}
                  onChange={(e) => handleChange('emisor_id', e.target.value)}
                  className={errors.emisor_id ? 'error' : ''}
                >
                  <option value="">Seleccionar emisor...</option>
                  {emisores.map(e => (
                    <option key={e.id} value={e.id}>
                      {e.rfc} - {e.razon_social}
                    </option>
                  ))}
                </select>
                {errors.emisor_id && <span className="error-message">{errors.emisor_id}</span>}
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>
                  Uso de CFDI <span className="required">*</span>
                </label>
                <select
                  value={formData.uso_cfdi}
                  onChange={(e) => handleChange('uso_cfdi', e.target.value)}
                  className={errors.uso_cfdi ? 'error' : ''}
                >
                  <option value="">Seleccionar...</option>
                  {catalogos.usosCFDI.map(u => (
                    <option key={u.clave} value={u.clave}>
                      {u.clave} - {u.descripcion}
                    </option>
                  ))}
                </select>
                {errors.uso_cfdi && <span className="error-message">{errors.uso_cfdi}</span>}
              </div>

              <div className="form-group">
                <label>
                  Metodo de Pago <span className="required">*</span>
                </label>
                <select
                  value={formData.metodo_pago}
                  onChange={(e) => handleChange('metodo_pago', e.target.value)}
                  className={errors.metodo_pago ? 'error' : ''}
                >
                  <option value="">Seleccionar...</option>
                  {catalogos.metodosPago.map(m => (
                    <option key={m.clave} value={m.clave}>
                      {m.clave} - {m.descripcion}
                    </option>
                  ))}
                </select>
                {errors.metodo_pago && <span className="error-message">{errors.metodo_pago}</span>}
              </div>
            </div>

            <div className="form-group">
              <label>
                Forma de Pago <span className="required">*</span>
              </label>
              <select
                value={formData.forma_pago}
                onChange={(e) => handleChange('forma_pago', e.target.value)}
                className={errors.forma_pago ? 'error' : ''}
              >
                <option value="">Seleccionar...</option>
                {catalogos.formasPago.map(f => (
                  <option key={f.clave} value={f.clave}>
                    {f.clave} - {f.descripcion}
                  </option>
                ))}
              </select>
              {errors.forma_pago && <span className="error-message">{errors.forma_pago}</span>}
            </div>
          </div>
        )}

        {/* Step 4: Confirmar */}
        {step === 4 && (
          <div className="wizard-content">
            <h3>Confirmar {numFacturas > 1 ? `${numFacturas} Facturas` : 'Factura'}</h3>

            {emisorSeleccionado && (
              <div className="detail-section" style={{ marginBottom: '1rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.5rem', padding: '1rem' }}>
                <h4 style={{ color: '#15803d', margin: '0 0 0.5rem' }}>Contribuyente Emisor</h4>
                <div className="detail-row">
                  <label>RFC:</label>
                  <span style={{ fontWeight: 600 }}>{emisorSeleccionado.rfc}</span>
                </div>
                <div className="detail-row">
                  <label>Razón Social:</label>
                  <span style={{ fontWeight: 600 }}>{emisorSeleccionado.razon_social}</span>
                </div>
              </div>
            )}

            <div className="factura-detail-grid">
              <div className="detail-section">
                <h4>Receptor</h4>
                <div className="detail-row">
                  <label>RFC:</label>
                  <span>{datosFiscales?.rfc}</span>
                </div>
                <div className="detail-row">
                  <label>Razon Social:</label>
                  <span>{datosFiscales?.razon_social}</span>
                </div>
              </div>

              <div className="detail-section">
                <h4>Datos de Pago</h4>
                <div className="detail-row">
                  <label>Uso CFDI:</label>
                  <span>
                    {catalogos.usosCFDI.find(u => u.clave === formData.uso_cfdi)?.descripcion || formData.uso_cfdi}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Forma de Pago:</label>
                  <span>
                    {catalogos.formasPago.find(f => f.clave === formData.forma_pago)?.descripcion || formData.forma_pago}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Metodo de Pago:</label>
                  <span>
                    {catalogos.metodosPago.find(m => m.clave === formData.metodo_pago)?.descripcion || formData.metodo_pago}
                  </span>
                </div>
              </div>
            </div>

            {/* Mostrar distribución si hay múltiples facturas */}
            {numFacturas > 1 && (
              <div style={{ marginTop: '1rem', padding: '1rem', background: '#eef2ff', borderRadius: '0.5rem' }}>
                <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', color: '#4338ca' }}>
                  <Split size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                  Se generarán {numFacturas} facturas:
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {distribucionFacturas.map((f) => (
                    <span key={f.numero} style={{
                      padding: '0.25rem 0.75rem',
                      background: 'white',
                      borderRadius: '0.25rem',
                      fontSize: '0.875rem',
                      border: '1px solid #c7d2fe'
                    }}>
                      #{f.numero}: {formatMoney(f.total)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="factura-totales" style={{ marginTop: '1rem' }}>
              {tipoPago && pago ? (
                <>
                  <div className="total-row">
                    <label>Folio Pago:</label>
                    <span style={{ color: '#667eea', fontWeight: 600 }}>{pago.folio}</span>
                  </div>
                </>
              ) : (
                <div className="total-row">
                  <label>Ventas incluidas:</label>
                  <span>{ventas?.length || 0}</span>
                </div>
              )}
              <div className="total-row">
                <label>Facturas a generar:</label>
                <span style={{ fontWeight: 600, color: '#667eea' }}>{numFacturas}</span>
              </div>
              <div className="total-row final">
                <label>TOTAL:</label>
                <span>{formatMoney(totales.total)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Exito */}
        {step === 5 && facturasCreadas.length > 0 && (
          <div className="wizard-content" style={{ textAlign: 'center', padding: '2rem' }}>
            <CheckCircle size={64} color="#059669" style={{ marginBottom: '1rem' }} />
            <h3 style={{ color: '#059669', marginBottom: '0.5rem' }}>
              {facturasCreadas.length > 1 ? `${facturasCreadas.length} Facturas Creadas` : 'Factura Creada'}
            </h3>
            <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
              {facturasCreadas.length > 1
                ? 'Las facturas han sido generadas exitosamente'
                : 'La factura ha sido generada exitosamente'}
            </p>

            {emisorSeleccionado && (
              <div style={{ textAlign: 'left', marginBottom: '1rem', padding: '0.75rem 1rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.5rem', maxWidth: '400px', margin: '0 auto 1rem' }}>
                <div style={{ fontWeight: 600, color: '#15803d', fontSize: '0.8rem', marginBottom: '0.25rem' }}>Emisor</div>
                <div style={{ fontSize: '0.9rem' }}>{emisorSeleccionado.rfc} - {emisorSeleccionado.razon_social}</div>
              </div>
            )}

            <div style={{
              display: 'grid',
              gridTemplateColumns: facturasCreadas.length > 1 ? 'repeat(auto-fit, minmax(200px, 1fr))' : '1fr',
              gap: '1rem',
              maxWidth: facturasCreadas.length > 1 ? '600px' : '400px',
              margin: '0 auto'
            }}>
              {facturasCreadas.map((factura, idx) => (
                <div key={idx} className="detail-section" style={{ textAlign: 'left' }}>
                  {facturasCreadas.length > 1 && (
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                      Factura {idx + 1} de {facturasCreadas.length}
                    </div>
                  )}
                  <div className="detail-row">
                    <label>Folio:</label>
                    <span style={{ fontWeight: 'bold', color: '#667eea' }}>{factura.folio}</span>
                  </div>
                  {factura.uuid && (
                    <div className="detail-row">
                      <label>UUID:</label>
                      <span style={{ fontWeight: 'bold', fontSize: '0.8rem', color: '#059669', wordBreak: 'break-all' }}>{factura.uuid}</span>
                    </div>
                  )}
                  <div className="detail-row">
                    <label>Total:</label>
                    <span style={{ fontWeight: 'bold', color: '#059669' }}>{formatMoney(factura.total)}</span>
                  </div>
                  <div className="detail-row">
                    <label>Estado:</label>
                    <span className={`estado-badge ${factura.estado === 'timbrada' ? 'timbrada' : 'pendiente_timbrado'}`}>
                      {factura.estado === 'timbrada' ? 'Timbrada' : 'Pendiente'}
                    </span>
                  </div>
                  {factura.timbrado_error && (
                    <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.25rem', fontSize: '0.8rem', color: '#991b1b' }}>
                      {factura.timbrado_error}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {facturasCreadas.some(f => f.estado === 'timbrada') && (
              <p style={{ marginTop: '1.5rem', fontSize: '0.875rem', color: '#059669' }}>
                {facturasCreadas.filter(f => f.estado === 'timbrada').length === facturasCreadas.length
                  ? 'Todas las facturas fueron timbradas exitosamente.'
                  : 'Algunas facturas fueron timbradas. Las pendientes se pueden reintentar.'}
              </p>
            )}
            {facturasCreadas.every(f => f.estado !== 'timbrada') && (
              <p style={{ marginTop: '1.5rem', fontSize: '0.875rem', color: '#dc2626' }}>
                No se pudieron timbrar las facturas. Se guardaron como pendientes de timbrado.
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="wizard-actions">
          {step < 5 ? (
            <>
              <button
                type="button"
                className="btn-cancelar"
                onClick={step === 1 ? onClose : handleBack}
              >
                {step === 1 ? 'Cancelar' : 'Anterior'}
              </button>

              {step < 4 ? (
                <button
                  type="button"
                  className="btn-guardar"
                  onClick={handleNext}
                >
                  Siguiente
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-guardar"
                  onClick={handleGenerarFacturas}
                  disabled={loading}
                  style={{ background: '#059669', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <FileText size={16} />
                  {loading
                    ? 'Generando...'
                    : numFacturas > 1
                      ? `Generar ${numFacturas} Facturas`
                      : 'Generar Factura'}
                </button>
              )}
            </>
          ) : (
            <button
              type="button"
              className="btn-guardar"
              onClick={onSuccess}
              style={{ marginLeft: 'auto' }}
            >
              Cerrar
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default NuevaFacturaModal;
